import type { ReportSendStatus } from './reportStatus';
import { DEFAULT_MATCH_TOLERANCE_PCT, matchTolerance } from './reportFormulas';
import type { BillWorkflowStatus, WorkflowStep } from './billWorkflow';
import type { BillRunCoverage, MonthMode } from './billRunRows';
import type { BillRunDetail, BillRunStatusValue } from './billRunStatus';
import { BILL_RUNS_SORT_KEYS, type BillRunsSort, type BillRunsSortKey } from './billRunsSort';
import {
  NO_RUN_CONDITIONS, NO_RUN_SITE_FILTERS,
  ENROLMENT_ORDER,
  type BillRunConditions, type BillRunEnrolment, type BillRunSiteFilters, type VarianceDirection,
} from './billRunConditions';

// Bill Runs keeps everything in the URL (FR-10), so a link reproduces the view.
// Its own keys, prefixed `br`, so it never reads or writes the shared
// filters the current Bills / Bulk Bill Run / Bulk Customer Report pages use.

export type BillRunsTab = 'bills' | 'reports' | 'missing' | 'insights';
export const BILL_RUNS_TABS: BillRunsTab[] = ['bills', 'reports', 'missing', 'insights'];

export type BillRunsView = {
  /** Which tab is showing; Bills when absent. */
  tab: BillRunsTab;
  /** `YYYY-MM`; both empty until the user picks months. */
  from: string;
  to: string;
  mode: MonthMode;
  site: BillRunSiteFilters;
  conditions: BillRunConditions;
  /** Column sort; null keeps the default order. */
  sort: BillRunsSort | null;
  /** Match tolerance %: within it a bill is Matched. Default 5, can only be widened. */
  tolerance: number;
};

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const list = (v: string | null): string[] => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
const num = (v: string | null): number | null => {
  if (v == null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};
const DIRECTIONS: VarianceDirection[] = ['over', 'under', 'either'];

export function readBillRunsView(p: URLSearchParams): BillRunsView {
  const from = p.get('brFrom') ?? '';
  const to = p.get('brTo') ?? '';
  const minDollars = num(p.get('brVarMin'));
  const minPct = num(p.get('brVarPct'));
  const dir = p.get('brVarDir') as VarianceDirection | null;
  const hasVariance = minDollars != null || minPct != null || (dir != null && DIRECTIONS.includes(dir) && dir !== 'either');
  const tab = p.get('brTab') as BillRunsTab | null;
  const sortKey = p.get('brSort') as BillRunsSortKey | null;
  return {
    tab: tab && BILL_RUNS_TABS.includes(tab) ? tab : 'bills',
    from: MONTH.test(from) ? from : '',
    to: MONTH.test(to) ? to : '',
    mode: p.get('brMode') === 'end' ? 'end' : 'period',
    site: {
      q: p.get('brQ') ?? '',
      entity: p.get('brEntity') ?? '',
      retailer: list(p.get('brRetailer')),
      distributor: p.get('brDistributor') ?? '',
      state: p.get('brState') ?? '',
      tariff: p.get('brTariff') ?? '',
    },
    conditions: {
      ...NO_RUN_CONDITIONS,
      status: list(p.get('brStatus')) as BillRunStatusValue[],
      details: list(p.get('brDetails')) as BillRunDetail[],
      assigned: list(p.get('brAssigned')) as WorkflowStep[],
      send: list(p.get('brSend')) as ReportSendStatus[],
      workflow: list(p.get('brWorkflow')) as BillWorkflowStatus[],
      coverage: list(p.get('brCoverage')) as BillRunCoverage[],
      neverBilled: p.get('brNever') === '1',
      enrolment: list(p.get('brEnrol')).filter((e): e is BillRunEnrolment => ENROLMENT_ORDER.includes(e as BillRunEnrolment)),
      billRunId: p.get('brRun') || null,
      driver: p.get('brDriver') || null,
      variance: hasVariance
        ? { minDollars, minPct, direction: dir && DIRECTIONS.includes(dir) ? dir : 'either' }
        : null,
      includeArchived: p.get('brArchived') === '1',
    },
    sort: sortKey && BILL_RUNS_SORT_KEYS.includes(sortKey)
      ? { key: sortKey, dir: p.get('brDir') === 'desc' ? 'desc' : 'asc' }
      : null,
    tolerance: matchTolerance(p.get('brTol') ?? DEFAULT_MATCH_TOLERANCE_PCT),
  };
}

/** The URL params for a view; empty values are left out so links stay short. */
export function writeBillRunsView(v: BillRunsView): URLSearchParams {
  const p = new URLSearchParams();
  const set = (k: string, val: string | null | undefined) => { if (val) p.set(k, val); };
  if (v.tab !== 'bills') p.set('brTab', v.tab);
  set('brFrom', v.from);
  set('brTo', v.to);
  if (v.mode === 'end') p.set('brMode', 'end');
  set('brQ', v.site.q);
  set('brEntity', v.site.entity);
  set('brRetailer', v.site.retailer.join(','));
  set('brDistributor', v.site.distributor);
  set('brState', v.site.state);
  set('brTariff', v.site.tariff);
  const c = v.conditions;
  set('brStatus', c.status.join(','));
  set('brDetails', c.details.join(','));
  set('brAssigned', c.assigned.join(','));
  set('brSend', c.send.join(','));
  set('brWorkflow', c.workflow.join(','));
  set('brCoverage', c.coverage.join(','));
  if (c.neverBilled) p.set('brNever', '1');
  set('brEnrol', c.enrolment.join(','));
  set('brRun', c.billRunId);
  set('brDriver', c.driver);
  if (c.variance) {
    if (c.variance.minDollars != null) p.set('brVarMin', String(c.variance.minDollars));
    if (c.variance.minPct != null) p.set('brVarPct', String(c.variance.minPct));
    p.set('brVarDir', c.variance.direction);
  }
  if (c.includeArchived) p.set('brArchived', '1');
  if (v.sort) {
    p.set('brSort', v.sort.key);
    p.set('brDir', v.sort.dir);
  }
  if (v.tolerance !== DEFAULT_MATCH_TOLERANCE_PCT) p.set('brTol', String(v.tolerance));
  return p;
}

/** Every `YYYY-MM` from `from` to `to` inclusive; [] when either is missing or to < from. */
export function monthRange(from: string, to: string): string[] {
  if (!MONTH.test(from) || !MONTH.test(to) || to < from) return [];
  const out: string[] = [];
  let [y, m] = from.split('-').map(Number);
  for (;;) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push(key);
    if (key === to) return out;
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
}

/** The last completed calendar month, the default (`today` injectable for tests). */
export function lastCompletedMonth(today: Date = new Date()): string {
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export const EMPTY_VIEW: BillRunsView = {
  tab: 'bills', from: '', to: '', mode: 'period', site: NO_RUN_SITE_FILTERS, conditions: NO_RUN_CONDITIONS, sort: null,
  tolerance: DEFAULT_MATCH_TOLERANCE_PCT,
};
